import { DEFAULT_PLAN_AI_CREDIT, DEFAULT_PLAN_SEATS, DEFAULT_PRICING, EMPTY_SUBSCRIPTION, PLANS, isPlan, parsePricing, type LockedPrices, type Plan, type SubscriptionState, type TrialUsage } from "@szerviz/core";
import type { MySubscription, OpSubscription, OpWorkshop } from "./types";

type Raw = Record<string, unknown>;
const num = (v: unknown, d = 0) => (v === null || v === undefined || v === "" || Number.isNaN(Number(v)) ? d : Number(v));
const numOrNull = (v: unknown) => (v === null || v === undefined ? null : Number(v));
const day = (v: unknown) => (typeof v === "string" && v ? v.slice(0, 10) : null);
const perPlan = (v: unknown, d: Record<Plan, number>): Record<Plan, number> => {
  const o = (v && typeof v === "object" ? v : {}) as Raw;
  return Object.fromEntries(PLANS.map((p) => [p, num(o[p], d[p])])) as Record<Plan, number>;
};

export function parseLockedPrices(v: unknown): LockedPrices | null {
  if (!v || typeof v !== "object") return null;
  const o = v as Raw;
  return { plans: perPlan(o.plans, DEFAULT_PRICING.plans), extraUserNet: num(o.extraUserNet, DEFAULT_PRICING.extraUserNet) };
}

/** The trial's usage counts (null: not in the trial). */
export function parseTrialUsage(v: unknown): TrialUsage | null {
  if (!v || typeof v !== "object") return null;
  const o = v as Raw;
  return { quoteLinks: num(o.quoteLinks), stockMovements: num(o.stockMovements), reminders: num(o.reminders) };
}

/** The my_subscription() answer as the app uses it (missing fields: the defaults). */
export function parseMySubscription(raw: unknown): MySubscription {
  const r = (raw && typeof raw === "object" ? raw : {}) as Raw;
  const plan = isPlan(r.plan) ? r.plan : "basic";
  return {
    ...EMPTY_SUBSCRIPTION,
    today: day(r.today) ?? new Date().toISOString().slice(0, 10),
    plan,
    effectivePlan: isPlan(r.effectivePlan) ? r.effectivePlan : plan,
    readOnly: r.readOnly === true,
    trialUntil: day(r.trialUntil),
    trialStarted: day(r.trialStarted),
    trialUpgradeUntil: day(r.trialUpgradeUntil),
    trialUpgradePlan: isPlan(r.trialUpgradePlan) ? r.trialUpgradePlan : null,
    trialUsage: parseTrialUsage(r.trialUsage),
    planAfterTrial: isPlan(r.planAfterTrial) ? r.planAfterTrial : null,
    period: r.period === "yearly" ? "yearly" : "monthly",
    extraSeats: num(r.extraSeats),
    billingAnchor: day(r.billingAnchor),
    founder: r.founder === true,
    founderUntil: day(r.founderUntil),
    priceLockUntil: day(r.priceLockUntil),
    lockedPrices: parseLockedPrices(r.lockedPrices),
    status: typeof r.status === "string" ? r.status : "trial",
    monthlyFee: numOrNull(r.monthlyFee),
    discountPct: numOrNull(r.discountPct),
    pricing: parsePricing(r.pricing) ?? DEFAULT_PRICING,
    seats: perPlan(r.seats, DEFAULT_PLAN_SEATS),
    aiCredit: perPlan(r.aiCredit, DEFAULT_PLAN_AI_CREDIT),
    seatsUsed: num(r.seatsUsed),
    seatLimit: num(r.seatLimit, 100000),
    smsNet: num(r.smsNet),
    aiCreditGross: num(r.aiCreditGross),
  };
}

/** The subscription state of a workshop on the operator pages (list row + op_subscriptions row). */
export function opSubscriptionState(w: Pick<OpWorkshop, "plan" | "status" | "monthlyFee" | "discountPct">, s: OpSubscription | undefined): SubscriptionState {
  return {
    ...EMPTY_SUBSCRIPTION,
    plan: w.plan,
    status: w.status,
    monthlyFee: w.monthlyFee,
    discountPct: w.discountPct,
    ...(s
      ? {
          trialUntil: s.trialUntil, planAfterTrial: s.planAfterTrial, period: s.period, extraSeats: s.extraSeats, billingAnchor: s.billingAnchor,
          founder: s.founder, founderUntil: s.founderUntil, priceLockUntil: s.priceLockUntil, lockedPrices: s.lockedPrices,
        }
      : {}),
  };
}
